import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../config";
import { GET_PRODUCT_BY_HANDLE, GET_COLLECTIONS_BY_HANDLE_FILTER_BY_TAG } from "../graphqlQueries";
import { IGProduct, IGCollectionsProduct } from "../types";
import { getAdminIdFromStoreFrontId } from "../helpers/shopify";

/**
 * Fetches a product by handle from Shopify's GraphQL API
 * @param productHandle - The product handle to fetch
 * @returns Promise<IGProduct | null> - The product data or null if not found
 */
export const fetchProductByHandle = async (productHandle: string): Promise<IGProduct | null> => {
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: GET_PRODUCT_BY_HANDLE,
        variables: {
          handle: productHandle,
        },
      }),
    });

    const data = await response.json();
    let product: any = data?.data?.product || {};

    if (!product.id) {
      return null;
    }

    product.variants = product?.variants?.edges?.map((e: any) => e.node) || [];
    product.collections =
      product?.collections && product?.collections?.nodes?.map((collection: any) => (collection.id ? getAdminIdFromStoreFrontId(collection.id as string) : collection.id));

    return product as IGProduct;
  } catch (error) {
    console.error("Error fetching product:", error);
    return null;
  }
};

/**
 * Fetches collections by handle from Shopify's GraphQL API
 * @param query - The query string to filter collections (e.g., "handle:main-track")
 * @returns Promise<IGCollectionsProduct[] | null> - The collection products or null if not found
 */
export const fetchCollectionsByHandle = async (query: string): Promise<IGCollectionsProduct[] | null> => {
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: GET_COLLECTIONS_BY_HANDLE_FILTER_BY_TAG,
        variables: {
          query,
        },
      }),
    });

    const data = await response.json();

    if (data.errors) {
      console.error("GraphQL Error:", data.errors);
      return null;
    }

    const collectionProducts: any = (data?.data?.collections?.nodes || [])[0]?.products?.nodes || [];
    return collectionProducts;
  } catch (error) {
    console.error("Error fetching collections:", error);
    return null;
  }
};

/**
 * Alias for fetchProductByHandle for backward compatibility
 * @param productHandle - The product handle to fetch
 * @returns Promise<IGProduct | null> - The product data or null if not found
 */
export const getProduct = fetchProductByHandle;
